import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  Building2,
  Clock,
  CloudUpload,
  Image as ImageIcon,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { Layout } from '../components/Layout.tsx';
import { advertisementAPI } from '../services/api.ts';
import { useDropzone } from 'react-dropzone';
import { uploadToCloudinary } from '../services/index.tsx';
import {
  ConfirmModal,
  EmptyState,
  PageHeader,
} from '../components/app/index.ts';

const MEDIA_HEIGHT = 192;

// ----------------------
// Drop area (drop logic stays with react-dropzone)
// ----------------------
type DropAreaProps = {
  dropzone: ReturnType<typeof useDropzone>;
  title: string;
  hint: string;
};

const DropArea: React.FC<DropAreaProps> = ({ dropzone, title, hint }) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone;
  return (
    <Paper
      {...getRootProps()}
      withBorder
      p="xl"
      ta="center"
      bg={isDragActive ? 'var(--mantine-color-default-hover)' : undefined}
      style={{ borderStyle: 'dashed', cursor: 'pointer' }}
    >
      <input {...getInputProps()} />
      <Stack align="center" gap={4}>
        <Text c="dimmed" lh={1}>
          <CloudUpload size={40} />
        </Text>
        {isDragActive ? (
          <Text fw={500} size="sm">
            Drop the image here...
          </Text>
        ) : (
          <>
            <Text fw={500} size="sm">
              {title}
            </Text>
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          </>
        )}
      </Stack>
    </Paper>
  );
};

// ----------------------
// Add Advertisement Modal
// ----------------------
type AddAdvertisementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
};

const AddAdvertisementModal: React.FC<AddAdvertisementModalProps> = ({
  isOpen,
  onClose,
  onAdded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);
  const dropzone = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    multiple: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an image file.');
      return;
    }

    if (!time) {
      setError('Please enter a time or schedule.');
      return;
    }

    const uploadImage = uploadToCloudinary(file, 'advertisement');
    const imageUrl = await uploadImage;
    if (!imageUrl) {
      setError('Failed to upload image.');
      return;
    }

    const formData = {
      url: imageUrl,
      time,
      type: 'image',
    };

    try {
      setLoading(true);
      await advertisementAPI.create(formData);
      onAdded();
      onClose();
      setFile(null);
      setTime('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to add advertisement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Text fw={600}>Add Advertisement</Text>}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <DropArea
            dropzone={dropzone}
            title={file ? file.name : 'Drag & drop an image here'}
            hint="or click to select one"
          />

          <TextInput
            label="Time / Schedule"
            leftSection={<Clock size={16} />}
            value={time}
            onChange={(e) => setTime(e.currentTarget.value)}
            placeholder="e.g., 30 seconds, 1 minute"
          />

          {error && (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          )}

          <Button type="submit" fullWidth loading={loading}>
            Add Advertisement
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};

// ----------------------
// Update Advertisement Modal
// ----------------------
type UpdateAdvertisementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  advertisement: any;
  onUpdated: () => void;
  onDeleted: () => void;
};

const UpdateAdvertisementModal: React.FC<UpdateAdvertisementModalProps> = ({
  isOpen,
  onClose,
  advertisement,
  onUpdated,
  onDeleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [time, setTime] = useState(advertisement?.time || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (advertisement) {
      setTime(advertisement.time || '');
    }
  }, [advertisement]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);
  const dropzone = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    multiple: false,
  });

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!time) {
      setError('Please enter a time or schedule.');
      return;
    }

    let imageUrl = advertisement.url;
    if (file) {
      const uploadImage = uploadToCloudinary(file, 'advertisement');
      imageUrl = await uploadImage;
      if (!imageUrl) {
        setError('Failed to upload image.');
        return;
      }
    }
    const formData = {
      url: imageUrl,
      time,
      type: 'image',
    };

    try {
      setLoading(true);
      await advertisementAPI.update(advertisement._id, formData);
      onUpdated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update advertisement');
    } finally {
      setLoading(false);
    }
  };

  // Runs once the delete has been confirmed.
  const handleDelete = async () => {
    if (!advertisement) return;

    try {
      setLoading(true);
      await advertisementAPI.delete(advertisement._id);
      onDeleted();
      onClose();
    } catch (err: any) {
      setConfirmDelete(false);
      setError(err.message || 'Failed to delete advertisement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        title={<Text fw={600}>Update Advertisement</Text>}
      >
        <Stack gap="md">
          {/* Current Image Preview */}
          <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
            <Text size="sm" fw={500} mb="sm">
              Current Advertisement
            </Text>
            {advertisement.type === 'image' ? (
              <Image
                src={advertisement.url}
                alt="advertisement"
                h={MEDIA_HEIGHT}
                fit="contain"
                radius="md"
              />
            ) : (
              <Box
                component="video"
                src={advertisement.url}
                controls
                w="100%"
                h={MEDIA_HEIGHT}
                style={{ objectFit: 'contain' }}
              />
            )}
          </Paper>

          <form onSubmit={handleUpdate}>
            <Stack gap="md">
              <DropArea
                dropzone={dropzone}
                title={file ? file.name : 'Upload new image (optional)'}
                hint="Drag & drop or click to select"
              />

              <TextInput
                label="Time / Schedule"
                leftSection={<Clock size={16} />}
                value={time}
                onChange={(e) => setTime(e.currentTarget.value)}
                placeholder="e.g., 30 seconds, 1 minute"
              />

              {error && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}

              <Group gap="sm" wrap="nowrap">
                <Button type="submit" flex={1} loading={loading && !confirmDelete}>
                  Update
                </Button>
                <Button
                  type="button"
                  color="red"
                  leftSection={<Trash2 size={16} />}
                  disabled={loading}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Modal>

      <ConfirmModal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this advertisement?"
        confirmLabel="Delete"
        loading={loading}
      />
    </>
  );
};

// ----------------------
// Main Advertisement Page Component
// ----------------------
const Advertisement: React.FC = () => {
  const [advertisements, setAdvertisements] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [error, setError] = useState('');
  const [company_id, setCompanyId] = useState<number | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await advertisementAPI.getAll();
      setAdvertisements(response.data as never as any[]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch advertisements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const handleAdAdded = () => {
    fetchAdvertisements();
  };

  const handleAdUpdated = () => {
    fetchAdvertisements();
  };

  const handleAdDeleted = () => {
    fetchAdvertisements();
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedCompanyId = storedUser ? JSON.parse(storedUser) : null;
    if (storedCompanyId?.company_id) {
      setCompanyId(parseInt(storedCompanyId?.company_id));
    }
    if (storedCompanyId?.role) {
      setRole(storedCompanyId?.role);
    }
  }, []);

  const filteredAdvertisements = advertisements.filter((ad) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      ad.company?.name?.toLowerCase().includes(q) ||
      ad.time?.toLowerCase().includes(q) ||
      ad.type?.toLowerCase().includes(q)
    );
  });

  const hasOwnAd = !!advertisements.find(
    (ad) => ad.company?.id === company_id
  );

  return (
    <Layout>
      <PageHeader
        title="Advertisements"
        description="Manage your advertisement campaigns"
        actions={
          <>
            <TextInput
              type="search"
              w={{ base: '100%', sm: 260 }}
              placeholder="Search by company, time, type..."
              leftSection={<Search size={16} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
            />
            {!hasOwnAd && role === 'admin' && (
              <Button
                leftSection={<Plus size={16} />}
                onClick={() => setAddModalOpen(true)}
              >
                Add Advertisement
              </Button>
            )}
          </>
        }
      />

      <Stack gap="md">
        {hasOwnAd && (
          <Alert color="yellow" variant="light">
            You already have an advertisement. Please update it instead of
            creating a new one.
          </Alert>
        )}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack align="center" gap="xs" py={80}>
            <Loader />
            <Text c="dimmed" fw={500}>
              Loading advertisements...
            </Text>
          </Stack>
        ) : filteredAdvertisements.length === 0 ? (
          <Paper withBorder shadow="sm" p="lg">
            <EmptyState
              icon={<ImageIcon size={32} />}
              title={
                searchTerm
                  ? 'No matching advertisements'
                  : 'No advertisements yet'
              }
              description={
                searchTerm
                  ? 'Try a different search term.'
                  : 'Get started by creating your first advertisement'
              }
              action={
                !searchTerm &&
                role === 'admin' && (
                  <Button
                    mt="sm"
                    leftSection={<Plus size={16} />}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add Advertisement
                  </Button>
                )
              }
            />
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
            {filteredAdvertisements.map((ad) => (
              <Card key={ad._id} withBorder shadow="sm" padding="md">
                <Card.Section bg="var(--mantine-color-default-hover)">
                  {ad.type === 'image' ? (
                    <Image
                      src={ad.url}
                      alt="advertisement"
                      h={MEDIA_HEIGHT}
                      fit="contain"
                    />
                  ) : (
                    <Box
                      component="video"
                      src={ad.url}
                      controls
                      display="block"
                      w="100%"
                      h={MEDIA_HEIGHT}
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                </Card.Section>

                <Stack gap="xs" mt="md">
                  <Group gap="xs" wrap="nowrap" c="dimmed">
                    <Clock size={16} />
                    <Text size="sm" fw={500}>
                      {ad.time}
                    </Text>
                  </Group>
                  <Group gap="xs" wrap="nowrap" c="dimmed">
                    <Building2 size={16} />
                    <Text size="sm">{ad.company?.name || 'N/A'}</Text>
                  </Group>

                  <Button
                    variant="default"
                    fullWidth
                    mt="xs"
                    leftSection={<Pencil size={16} />}
                    onClick={() => {
                      setSelectedAd(ad);
                      setUpdateModalOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <AddAdvertisementModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdded={handleAdAdded}
      />
      {selectedAd && (
        <UpdateAdvertisementModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedAd(null);
          }}
          advertisement={selectedAd}
          onUpdated={handleAdUpdated}
          onDeleted={handleAdDeleted}
        />
      )}
    </Layout>
  );
};

export default Advertisement;
